import {
  Container,
  Stack,
  Text,
  Menu,
  MenuItem,
  Icon,
  Select,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import { BLACK, CONTAINER_MAX_WIDTH_1 } from 'src/constants/ui';
import { FaStoreAlt } from 'react-icons/fa';
import {
  MdApproval,
  MdCategory,
  MdOutlineAttachMoney,
  MdYoutubeSearchedFor,
  MdOutlineAssignmentInd,
  MdMan4,
  MdOutlinePersonOutline,
} from 'react-icons/md';
import { FaLink } from 'react-icons/fa6';
import { TbWorldWww } from 'react-icons/tb';
import NextLink from 'next/link';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useAuth } from 'src/utils/auth';
import { useContext, useEffect, useState } from 'react';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { CoverLoader, LoginPrompt } from 'src/components/atoms';
import CreateMarketplaceButton from 'src/components/molecules/admin/create-marketplace-button';
import PreviewModeBanner from 'src/components/atoms/preview-mode-banner';
import UpsellTierModal from 'src/components/atoms/upsell-tier-modal';
import { FEATURE_IDS, FeatureId } from 'src/constants/tier-features';
import { useTheme } from '@chakra-ui/react';
import { ADMIN_ORIGIN } from 'src/constants/config';
import HelpButton from 'src/components/atoms/help-button';

interface MartketplaceAdminLayoutProps {
  children: React.ReactNode;
  title: string;
  standalone?: boolean;
}

const MarketplaceAdminLayout = ({
  children,
  title,
  standalone = false,
}: MartketplaceAdminLayoutProps): JSX.Element => {
  const router = useRouter();
  const theme = useTheme();
  const { hasAdmin, hasStaff, authLoading, isAuthenticated, user, userLoaded } = useAuth();
  const { marketplace, categories, fetchCategories, isAffiliate } = useContext(MarketplaceContext);

  const isAdmin = hasAdmin(marketplace?.id);
  const isStaffUser = hasStaff();
  const previewAllowed = isAuthenticated && (isAdmin || isStaffUser);
  const showContent = !marketplace || marketplace?.status === 'active' || previewAllowed;

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<FeatureId | null>(null);

  useEffect(() => {
    if (isAdmin && categories.length === 0) {
      fetchCategories();
    }
  }, [marketplace?.id, categories, isAdmin]);

  useEffect(() => {
    if (marketplace?.id && !authLoading && !isAdmin && userLoaded) {
      if (!user) {
        router.push('/login');
      } else {
        router.push('/');
      }
    }
  }, [marketplace, isAdmin, authLoading, userLoaded]);

  useEffect(() => {
    if (isAffiliate && isAdmin) {
      window.location.href = ADMIN_ORIGIN;
    }
  }, [isAffiliate, isAdmin]);

  const hasConciergeCategory = categories.some(
    (category) => !category.isDisabled && category.functional?.lead_concierge === true,
  );

  const handleNavigation = (path: string) => {
    router.push(path);
  };

  const onUpgradeModalClose = () => {
    setIsUpgradeModalOpen(false);
    setUpgradeFeature(null);
  };

  function handleLockedFeatureClick(featureID: FeatureId) {
    setUpgradeFeature(featureID);
    setIsUpgradeModalOpen(true);
  }

  const autoPopulateListingsPath = '/admin/auto-populate-listings';

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={title} />
        <link rel="icon" href={marketplace?.favicon_image || '/marketplace.ico'} />
      </Head>
      {showContent && marketplace?.status === 'preview' && isAffiliate !== null && (
        <PreviewModeBanner
          launchPreviewRequested={Boolean(marketplace?.launch_preview_requested)}
          variant="admin"
          isAffiliate={isAffiliate}
        />
      )}
      <Container maxW={CONTAINER_MAX_WIDTH_1} my={{ base: 4, md: 6, lg: 8 }}>
        <Stack justify="flex-start" align="flex-start" spacing="20px">
          {!standalone && (
            <Flex w="100%" flexDirection={{ base: 'column', lg: 'row' }}>
              <Text
                lineHeight="1.33"
                fontWeight="semibold"
                fontSize="24px"
                as={'span'}
                wordBreak="break-word"
                style={{ fontFamily: theme.fonts.font_1 }}
              >
                Marketplace Admin
              </Text>
              <Spacer />
              <Flex
                alignItems="center"
                gap={4}
                flexDirection={{ base: 'column', md: 'row' }}
                mt={{ base: 6, md: 0 }}
              >
                <CreateMarketplaceButton mb={4} />
              </Flex>
            </Flex>
          )}
          <Stack
            direction={{ base: 'column', lg: 'row' }}
            justify="flex-start"
            align="flex-start"
            spacing="20px"
            width="100%"
          >
            {!standalone && (
              <Stack
                justify="flex-start"
                align="flex-start"
                spacing="4px"
                minWidth="230px"
                maxWidth="100%"
              >
                {/* Dropdown for mobile */}
                <Select
                  display={{ base: 'block', lg: 'none' }}
                  onChange={(e) => {
                    if (
                      ['/admin/auto-populate-listings', '/admin/queries'].includes(
                        e.target.value,
                      ) &&
                      marketplace?.tier?.features[FEATURE_IDS.LISTING_IMPORTS] === false
                    ) {
                      handleLockedFeatureClick(FEATURE_IDS.LISTING_IMPORTS);
                      return;
                    }
                    handleNavigation(e.target.value);
                  }}
                  value={router.asPath}
                >
                  <option value="/admin/info">Marketplace Info</option>
                  <option value="/admin/social-media">Social Media</option>
                  <option value="/admin/categories">Categories</option>
                  <option value={autoPopulateListingsPath}>Auto-Populate Listings</option>
                  <option value="/admin/approvals">Listing Approvals</option>
                  <option value="/admin/payout">Payout Info</option>
                  <option value="/admin/users">Users</option>
                  <option value="/admin/pages">Pages</option>
                  <option value="/admin/linkinbio">Link In Bio</option>
                  {hasConciergeCategory && <option value="/admin/concierge">Lead Concierge</option>}
                </Select>
                {/* Regular menu for desktop */}
                <Stack display={{ base: 'none', lg: 'block' }} minW="200px">
                  <Menu>
                    <NextLink href="/admin/info" prefetch={false}>
                      <MenuItem
                        icon={<Icon as={FaStoreAlt} boxSize={5} />}
                        bg={router.asPath === '/admin/info' ? 'gray.100' : ''}
                        color={router.asPath === '/admin/info' ? BLACK : ''}
                        borderRadius="md"
                      >
                        Marketplace Info
                      </MenuItem>
                    </NextLink>
                    <NextLink href="/admin/social-media" prefetch={false}>
                      <MenuItem
                        icon={<Icon as={MdOutlineAssignmentInd} boxSize={5} />}
                        bg={router.asPath === '/admin/social-media' ? 'gray.100' : ''}
                        color={router.asPath === '/admin/social-media' ? BLACK : ''}
                        borderRadius="md"
                      >
                        Social Media
                      </MenuItem>
                    </NextLink>
                    <NextLink href="/admin/categories" prefetch={false}>
                      <MenuItem
                        icon={<Icon as={MdCategory} boxSize={5} />}
                        bg={router.asPath === '/admin/categories' ? 'gray.100' : ''}
                        color={router.asPath === '/admin/categories' ? BLACK : ''}
                        borderRadius="md"
                      >
                        Categories
                      </MenuItem>
                    </NextLink>
                    {marketplace?.tier?.features[FEATURE_IDS.LISTING_IMPORTS] === false ? (
                      <MenuItem
                        icon={<Icon as={MdYoutubeSearchedFor} boxSize={5} />}
                        color="gray.400"
                        cursor="pointer"
                        borderRadius="md"
                        onClick={() => handleLockedFeatureClick(FEATURE_IDS.LISTING_IMPORTS)}
                        _hover={{ bg: 'gray.50' }}
                      >
                        Auto-Populate Listings
                      </MenuItem>
                    ) : (
                      <NextLink href={autoPopulateListingsPath} prefetch={false}>
                        <MenuItem
                          icon={<Icon as={MdYoutubeSearchedFor} boxSize={5} />}
                          bg={router.asPath === autoPopulateListingsPath ? 'gray.100' : ''}
                          color={router.asPath === autoPopulateListingsPath ? BLACK : ''}
                          borderRadius="md"
                        >
                          Auto-Populate Listings
                        </MenuItem>
                      </NextLink>
                    )}
                    <NextLink href="/admin/approvals" prefetch={false}>
                      <MenuItem
                        icon={<Icon as={MdApproval} boxSize={5} />}
                        bg={router.asPath === '/admin/approvals' ? 'gray.100' : ''}
                        color={router.asPath === '/admin/approvals' ? BLACK : ''}
                        borderRadius="md"
                      >
                        Listing Approvals
                      </MenuItem>
                    </NextLink>
                    <NextLink href="/admin/payout" prefetch={false}>
                      <MenuItem
                        icon={<Icon as={MdOutlineAttachMoney} boxSize={5} />}
                        bg={router.asPath === '/admin/payout' ? 'gray.100' : ''}
                        color={router.asPath === '/admin/payout' ? BLACK : ''}
                        borderRadius="md"
                      >
                        Payout Info
                      </MenuItem>
                    </NextLink>
                    <NextLink href="/admin/users" prefetch={false}>
                      <MenuItem
                        icon={<Icon as={MdOutlinePersonOutline} boxSize={5} />}
                        bg={router.asPath === '/admin/users' ? 'gray.100' : ''}
                        color={router.asPath === '/admin/users' ? BLACK : ''}
                        borderRadius="md"
                      >
                        Users
                      </MenuItem>
                    </NextLink>
                    <NextLink href="/admin/pages" prefetch={false}>
                      <MenuItem
                        icon={<Icon as={TbWorldWww} boxSize={5} />}
                        bg={router.asPath === '/admin/pages' ? 'gray.100' : ''}
                        color={router.asPath === '/admin/pages' ? BLACK : ''}
                        borderRadius="md"
                      >
                        Pages
                      </MenuItem>
                    </NextLink>
                    <NextLink href="/admin/linkinbio" prefetch={false}>
                      <MenuItem
                        icon={<Icon as={FaLink} boxSize={5} />}
                        bg={router.asPath === '/admin/linkinbio' ? 'gray.100' : ''}
                        color={router.asPath === '/admin/linkinbio' ? BLACK : ''}
                        borderRadius="md"
                      >
                        Link In Bio
                      </MenuItem>
                    </NextLink>
                    {hasConciergeCategory && (
                      <NextLink href="/admin/concierge" prefetch={false}>
                        <MenuItem
                          icon={<Icon as={MdMan4} boxSize={5} />}
                          bg={router.asPath === '/admin/concierge' ? 'gray.100' : ''}
                          color={router.asPath === '/admin/concierge' ? BLACK : ''}
                          borderRadius="md"
                        >
                          Lead Concierge
                        </MenuItem>
                      </NextLink>
                    )}
                  </Menu>
                </Stack>
              </Stack>
            )}
            <Stack
              padding={standalone ? '' : ['12px', '12px', '32px']}
              paddingTop={standalone ? '' : ['16px', '16px', '32px']}
              borderRadius="8px"
              justify="flex-start"
              align="flex-start"
              spacing="28px"
              borderColor={'gray.300'}
              borderWidth={standalone ? '' : '1px'}
              width="100%"
            >
              {(authLoading || !isAdmin || !userLoaded) && (
                <CoverLoader context="MarketplaceAdminLayout:CheckingAuth" />
              )}
              {isAdmin && <>{children}</>}
              {isAuthenticated && !user ? (
                <></>
              ) : (
                !authLoading &&
                !isAdmin && (
                  <LoginPrompt message="to edit settings. Only Admins can modify these settings." />
                )
              )}
            </Stack>
          </Stack>
        </Stack>
      </Container>

      {upgradeFeature && (
        <UpsellTierModal
          isOpen={isUpgradeModalOpen}
          onClose={onUpgradeModalClose}
          featureId={upgradeFeature}
        />
      )}
      {isAdmin && <HelpButton />}
    </>
  );
};

export default MarketplaceAdminLayout;
